import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import * as Sentry from '@sentry/nextjs';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useMeasure } from 'react-use';
import { spreadsheet as spreadsheetCore } from '@industriousapps/excelkits-core';
import { DataSource } from '@/components/Editor/modals/Data/hooks/useDataSourceSelect';
import { deselectEditingArea, selectEditingArea } from '@/components/Editor/hooks/useEditingAreas';
import metrics from '@/services/metrics';
import navConfig from '@/config/navigation';
import featureFlags from '@/config/features';
import { desktopDevices, facebook, instagram, linkedin, mobileDevices, paper, pinterest, productHunt } from '@/config/previewResolutions';
import { calculatorBlocks } from '@/config/wordpress';
import responsiveUtils from '@/utils/responsive';
import date from '@/utils/date';
import deepClone from '@/utils/deepClone';
import logger from '@/utils/logger';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { withChartBuilderDefaults } from '@/features/ChartBuilder/utils';
import useWidgetUpdate from '@/features/WidgetEdit/hooks/useWidgetUpdate';
import useDownloadWidgetsWorkbook from '@/hooks/useDownloadWidgetsWorkbook';
import useParseFileUpload from '@/components/Editor/hooks/useParseFileUpload';
import useGlobalSectionEditing from '@/components/Editor/hooks/useGlobalSectionEditing';
import useAuthModal from '@/features/Auth/AuthModal/useAuthModal';
import { useCopyImage } from '@/components/Editor/share/hooks/useCopyImage';
import useConfirmation from '@/components/ConfirmationPrompt/hooks/useConfirmation';
import useWidgetSaving from '@/features/WidgetEdit/hooks/useWidgetSaving';
import { ButtonV2 as Button } from '@/components/Button';
import ThemeProvider from '@/components/widget/Layout/ThemeProvider';
import GeneralFields from '@/components/Editor/modals/EditSection/GeneralFields';
import ChartFields from '@/components/Editor/modals/EditSection/ChartFields';
import LoadingModal from '@/components/loaders/Modal';
import AuthModal from '@/features/Auth/AuthModal';
import ConfirmationPrompt from '@/components/ConfirmationPrompt';
import { Form, FormFooter, Layout as EditorLayout, layoutStyles, Preview, PreviewTooltip, ChartShare, ContextEditor, CanvasControls, useZoom, useCompiledWorkbook, useEditingAreas, useEditingAreasState, useNavigation, useSelectionsEditing, useAutoSelectionsUpdate, useWorkbook } from '@/components/Editor';
import EditorGuide from '@/features/ChartBuilder/Editor/Guide';
import Gallery from '@/features/ChartBuilder/Gallery';
import { GALLERY_DATA_PANEL_ID, GALLERY_THEME_PANEL_ID, CHARTS_ASPECT_RATIO, GALLERY_CHARTS_PANEL_ID } from '@/features/ChartBuilder/settings';
import { useGallery } from '@/features/ChartBuilder/hooks/useGallery';
import useDataAI from '@/features/ChartBuilder/hooks/useDataAI';
import styles from '@/features/ChartBuilder/styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PREFIX = 'features: ChartBuilder: Editor:';
const WORKBOOK_NAME = 'Advanced Data Editor';
const PREVIEW_GROUPS = [facebook, instagram, linkedin, productHunt, pinterest, paper, mobileDevices, desktopDevices];
const WIDGET_SAVE_CONFIRMATION_ID = 'confirmation-save-before-continue';
const CONFIRMATION_MESSAGES = {
  widgetSave: 'Save your chart and data before continuing?'
}; // Lazy loaded, large UI components

const DataModal = dynamic(() => import('@/components/Editor/modals/Data'), {
  ssr: false,
  loadableGenerated: {
    webpack: () => [require.resolveWeak('@/components/Editor/modals/Data')]
  }
});
const SelectionsModal = dynamic(() => import('@/components/Editor/modals/Selections'), {
  ssr: false,
  loadableGenerated: {
    webpack: () => [require.resolveWeak('@/components/Editor/modals/Selections')]
  }
});

const ChartEditor = _ref => {
  var _targetSection$config, _widget$project;

  let {
    productName,
    widget,
    workbookName,
    workbookData: workbookFileSrc,
    workbookMeta: workbookMetaFileSrc,
    workbookUpdatedAt,
    workbookSyncedAt,
    workbookOrigin,
    workbookSyncConfig,
    gallerySettings,
    templatesTag,
    createForProject,
    settings: defaultSettings = {},
    isCreating = false,
    isPublic = false,
    widgetTags,
    navigation,
    reviews,
    children,
    onReviewsClick
  } = _ref;
  const {
    addToast: sendNotification
  } = useToast();
  const {
    user,
    isLoading: isLoadingUser
  } = useAuth();
  const {
    authModalType,
    changeAuthType,
    openModal: openAuthModal,
    closeModal: closeAuthModal
  } = useAuthModal(); // Responsive queries

  const [workspaceRef, {
    width: containerWidth
  }] = useMeasure();
  const isSmallWorkspace = useMemo(() => responsiveUtils.isSmall(containerWidth), [containerWidth]);
  const {
    0: settings,
    1: setSettings
  } = useState(defaultSettings);
  const {
    0: layoutResolution,
    1: setLayoutResolution
  } = useState();
  const {
    0: isShareModalOpen,
    1: setShareModalOpen
  } = useState(false);
  const {
    0: defaultShowDataEditorErrors,
    1: setDefaultShowDataEditorErrors
  } = useState(false); // Initial workbook data source for the advanced data editor
  // It should be set before opening the data editor

  const {
    0: defaultDataEditorSource,
    1: setDefaultDataEditorSource
  } = useState(undefined);
  const {
    zoomLevel,
    adjustZoomLevel
  } = useZoom();
  const {
    workbook,
    workbookMeta,
    name,
    upload: uploadWorkbook,
    replace: replaceWorkbook,
    workbookId,
    isWidgetWorkbookUpToDate,
    fileOrigin: workbookFileOrigin,
    errors: workbookErrors,
    localSave: workbookLocalSave,
    save: workbookSave,
    isLoading: isLoadingWorkbook,
    hasUpdates: hasWorkbookUpdates,
    getGoogleDriveFile
  } = useWorkbook({
    widget: widget,
    workbookName: workbookName,
    workbookSrc: workbookFileSrc,
    workbookFormatsSrc: workbookMetaFileSrc.formats,
    workbookGridsSrc: workbookMetaFileSrc.grids,
    workbookStylesSrc: workbookMetaFileSrc.styles,
    workbookCellDataSrc: workbookMetaFileSrc.cellData,
    fileOriginSrc: workbookOrigin,
    workbookUpdatedAt
  });
  const {
    compiledWorkbook
  } = useCompiledWorkbook({
    workbook
  });
  const {
    payload,
    updatedAt,
    isLoading: isLoadingWidget,
    hasUpdates: hasWidgetUpdates,
    save: widgetSave,
    replaceWidget,
    handleChange,
    handleFontsChange
  } = useWidgetUpdate(widget, compiledWorkbook, workbookMeta.formats, sendNotification); // Get first section ID to use as target

  const sectionId = useMemo(() => getSectionId(payload), [payload]);
  const {
    parseFileUpload
  } = useParseFileUpload({
    uploadFn: uploadWorkbook,
    onSuccess: () => {
      // Switch to advanced editing mode
      openAdvancedEditDataModal();
    }
  });
  const {
    isGenerating: isDataGenerating,
    generateData
  } = useDataAI();
  const targetSection = useMemo(() => (payload.sections || {})[sectionId] || {}, [payload.sections, sectionId]);
  const componentType = useMemo(() => targetSection.component || '', [targetSection]);
  const targetSectionTitle = ((_targetSection$config = targetSection.config) === null || _targetSection$config === void 0 ? void 0 : _targetSection$config.title) || '';
  const {
    0: isBasicEditingMode,
    1: setBasicEditingMode
  } = useState(true); // Download all workbook assets for selected template
  // and replace the current widget with the template

  const {
    0: selectedWidgetTemplate,
    1: setSelectedWidgetTemplate
  } = useState(null);
  const {
    status: widgetsWorkbookDownloadStatus
  } = useDownloadWidgetsWorkbook({
    widget: selectedWidgetTemplate,
    onSuccess: (_ref2, widgetTemplate) => {
      let {
        workbook,
        workbookData
      } = _ref2;
      replaceWorkbook(workbookData, undefined, workbook);
      replaceWidget(withChartBuilderDefaults(widgetTemplate, sectionId));
      setSelectedWidgetTemplate(null); // Track template selection

      metrics.sendEvent('exp_template_select', {
        id: widgetTemplate.id
      });
    },
    onError: workbookDownloadError => {
      setSelectedWidgetTemplate(null);
      sendNotification({
        description: 'Failed to clone the template, please try again',
        type: 'error'
      });
      Sentry.captureException(Error(`${PREFIX}: widgets workbook download error: ${workbookDownloadError}`));
    }
  });
  useEditingAreas({
    initialState: {
      widgetId: payload.id,
      sectionId: sectionId
    },
    widget: payload
  });
  const {
    activeEditingAreaMeta,
    activeSectionEditingArea
  } = useEditingAreasState();
  const {
    activeSelectionsType,
    activeSelections,
    activeLabelsSelections,
    activeLegendsSelections,
    isEditingSelections,
    selectionEditIsLabeled,
    selectionEditStrategy,
    selectionControls,
    onSelectionSetup,
    onSelectionTeardown,
    onSubmitSelection
  } = useSelectionsEditing();
  const {
    activeSheetDataValidationResult,
    onAutoSelectionsUpdate
  } = useAutoSelectionsUpdate({
    workbookData: workbook,
    columnStructure: settings.basicDataEditorColumnStructure
  });
  const handleConfigChange = useCallback((target, update) => handleChange(`sections.${sectionId}.config${target ? '.' : ''}${target}`, update), [handleChange, sectionId]);
  const handleEditSelectionsSetup = useCallback(function () {
    let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    if (!targetSection.component) {
      sendNotification({
        type: 'error',
        description: 'Almost there! To get started, choose the type of chart you want to use.'
      });
      return;
    }

    onSelectionSetup({
      widget: payload,
      section: targetSection,
      selectionsType: config.selectionsType
    });
  }, [payload, targetSection, sendNotification, onSelectionSetup]); // Subscribe to heading edit events

  useGlobalSectionEditing({
    onChange: handleConfigChange
  });
  const {
    modalHeaderTabs,
    isEditingData,
    activeModalHeaderTab,
    toggleActiveModal
  } = useNavigation({
    isEditingSelections,
    onSelectionsModalSetup: handleEditSelectionsSetup,
    onSelectionsModalTeardown: onSelectionTeardown
  }); // Open the selections modal without
  // specifying a selector to target

  const openSelectionsModal = useCallback(() => {
    deselectEditingArea(); // Remove single selection focus

    selectEditingArea({
      sectionId
    }); // Restore target section focus

    setTimeout(() => {
      toggleActiveModal(modalHeaderTabs[1]);
    });
  }, [modalHeaderTabs, sectionId, toggleActiveModal]); // Opens the advanced data editor, with options

  const openAdvancedEditDataModal = useCallback(function () {
    let {
      showErrors,
      dataSource
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    setDefaultShowDataEditorErrors(Boolean(showErrors));
    setDefaultDataEditorSource(dataSource);
    toggleActiveModal(modalHeaderTabs[0]);
  }, [modalHeaderTabs, toggleActiveModal]);
  const {
    activeGalleryPanel,
    selectGalleryItem,
    closeGalleryPanel
  } = useGallery({
    isBasicEditingMode,
    onOpenAdvancedDataEditor: openAdvancedEditDataModal,
    onOpenUpload: () => openAdvancedEditDataModal({
      dataSource: DataSource.LOCAL_FILE
    }),
    onSignup: () => handleAuthModalOpen('signup')
  }); // Open either the basic or advanced data editor

  const openDataEditor = useCallback(function () {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    if (isBasicEditingMode) {
      selectGalleryItem(GALLERY_DATA_PANEL_ID);
    } else {
      openAdvancedEditDataModal(options);
    }
  }, [isBasicEditingMode, openAdvancedEditDataModal, selectGalleryItem]); // Open the share modal and track the event

  const openShareModal = useCallback(() => {
    metrics.sendEvent('chart_share_start', {
      chart: window.location.pathname
    });
    setShareModalOpen(true);
  }, []); // Provide user any error message intended for them
  // log the error to the console and send an error
  // report to error reporting API

  const handleUserFacingError = useCallback(err => {
    sendNotification({
      type: 'error',
      description: err.userMessage || 'Unexpected error, please try again or contact support'
    });
    logger.error(`${PREFIX} handleUserFacingError: ${err}`);
    Sentry.captureException(Error(`${PREFIX}: handleUserFacingError: ${err}`));
  }, [sendNotification]);
  const previewCanvasRef = useRef(null);
  const lastSharedWidgetUpdate = useRef(-1); // Redirect user to the widgets page after saving

  const redirectAfterSave = useCallback(widgetId => {
    window.location.href = widgetId ? `${navConfig.chartsPage}/${widgetId}` : navConfig.defaultPrivatePage;
  }, []);
  const {
    saveWidgetUpdates
  } = useWidgetSaving({
    isCreating: isCreating,
    disableWidgetSave: !user || isLoadingUser,
    disableWorkbookSave: !user || isLoadingUser,
    workbookSave,
    widgetSave,
    reportingWidget: widget,
    createForProject,
    onSuccess: widgetId => {
      // Redirect user to their new chart
      if (isCreating && widgetId) {
        redirectAfterSave(widgetId);
      }

      sendNotification({
        description: isCreating ? 'Redirecting to your new chart' : 'Updates saved',
        type: 'success'
      });
    }
  }); // Prepare the payload to create a new widget

  const widgetCreatePayload = useMemo(() => {
    const clone = deepClone(payload);
    clone.updatedAt = updatedAt; // Remove default widget properties

    delete clone.id;
    delete clone.workbookFileRef; // Set the default widget title

    clone.title = targetSectionTitle || `Chart ${date.toDateStamp()}`; // Add chart builder tags to the widget

    clone.tags = [...(clone.tags || []), ...(widgetTags || [])];
    return {
      widget: clone,
      workbook,
      workbookId,
      name: workbookName,
      workbookMeta,
      workbookFileOrigin
    };
  }, [payload, updatedAt, workbook, workbookId, workbookName, workbookMeta, workbookFileOrigin, widgetTags, targetSectionTitle]);
  const {
    openConfirmationPrompt: promptForWidgetSave
  } = useConfirmation({
    id: WIDGET_SAVE_CONFIRMATION_ID,
    title: CONFIRMATION_MESSAGES.widgetSave,
    onCancel: () => redirectAfterSave(),
    onConfirm: () => saveWidgetUpdates(widgetCreatePayload)
  });
  const handleAuthModalOpen = useCallback(authType => {
    if (user) {
      promptForWidgetSave();
      return;
    }

    openAuthModal(authType);
  }, [user, openAuthModal, promptForWidgetSave]);
  const handleAuthSuccess = useCallback(function () {
    let isNewUser = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    if (isNewUser) {
      // Track new user sign up
      metrics.sendEvent('exp_chart_plan', {}, {
        maxEventsPerSession: 1
      });
    }

    closeAuthModal();
    promptForWidgetSave();
  }, [closeAuthModal, promptForWidgetSave]);
  const handleWidgetSave = useCallback(() => {
    if (user) {
      saveWidgetUpdates(widgetCreatePayload);
      return;
    }

    openAuthModal('signup');
  }, [user, widgetCreatePayload, saveWidgetUpdates, openAuthModal]); // Track the share event success

  const sendChartShareSuccessEvent = useCallback(type => {
    if (updatedAt <= 0 || updatedAt === lastSharedWidgetUpdate.current) {
      return;
    }

    metrics.sendEvent('chart_share_success', {
      chart: window.location.pathname,
      type: type
    }, {
      maxEventsPerSession: 1
    });
    lastSharedWidgetUpdate.current = updatedAt;
  }, [updatedAt]);
  const handleCopyImageSuccess = useCallback(() => {
    sendChartShareSuccessEvent('image');
    sendNotification({
      toastId: 'copy-image-success',
      type: 'success',
      description: 'Successfully copied image to your clipboard',
      position: 'bottomMiddle'
    });
  }, [sendChartShareSuccessEvent, sendNotification]);
  const handleChartShareSuccess = useCallback(type => {
    sendChartShareSuccessEvent(type);
    sendNotification({
      type: 'success',
      description: `Successfully downloaded ${type}`,
      position: 'bottomMiddle'
    });
  }, [sendChartShareSuccessEvent, sendNotification]);
  const handleChartShareSignup = useCallback(type => {
    setShareModalOpen(false);

    if (!user) {
      openAuthModal('signup');
      return;
    }

    const isWordpressEmbed = type === 'wordpress_embed_link';

    if (isWordpressEmbed) {
      window.open(calculatorBlocks.profileUrl, '_blank');
      return;
    }

    promptForWidgetSave();
  }, [user, openAuthModal, promptForWidgetSave]);
  const {
    handleCopyImage
  } = useCopyImage({
    captureElement: previewCanvasRef,
    onImageCopy: handleCopyImageSuccess,
    onError: handleUserFacingError
  });
  const handleBasicDataEditorChange = useCallback(wb => {
    workbookLocalSave(wb);
    setDefaultShowDataEditorErrors(false);
    onAutoSelectionsUpdate(handleChange, _objectSpread(_objectSpread({}, targetSection), {}, {
      id: sectionId
    }), wb);
  }, [sectionId, targetSection, onAutoSelectionsUpdate, handleChange, workbookLocalSave]); // Handle chart selection from the gallery
  // and update the widget with the new chart

  const handleChartSelection = useCallback((adapter, settings) => {
    if (typeof adapter !== 'function') {
      throw new Error('Invalid chart adapter');
    }

    const changedWidget = adapter(sectionId, payload);
    replaceWidget(changedWidget);
    setSettings(settings);

    if (activeGalleryPanel === GALLERY_CHARTS_PANEL_ID) {
      closeGalleryPanel();
    }
  }, [activeGalleryPanel, sectionId, payload, replaceWidget, closeGalleryPanel]);

  const handleSaving = async function () {
    let publish = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    saveWidgetUpdates(isCreating ? widgetCreatePayload : {
      name,
      workbookId,
      workbook,
      workbookMeta,
      workbookFileOrigin,
      widget: payload
    }, publish);
  };

  const isWorkbookSetup = useMemo(() => spreadsheetCore.hasContent(workbook), [workbook]);
  const isChartSetup = Boolean(targetSection === null || targetSection === void 0 ? void 0 : targetSection.component);
  const isSetupComplete = isWorkbookSetup && isChartSetup;
  const hasUpdates = hasWidgetUpdates || hasWorkbookUpdates;
  const isLoading = widgetsWorkbookDownloadStatus === 'loading' || isLoadingWidget || isLoadingWorkbook;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ThemeProvider, {
      config: payload.config,
      children: /*#__PURE__*/_jsxs(EditorLayout, {
        children: [typeof navigation === 'function' ? navigation({
          widget: payload,
          projectId: widget === null || widget === void 0 ? void 0 : (_widget$project = widget.project) === null || _widget$project === void 0 ? void 0 : _widget$project.id,
          hasUpdates,
          isLoading,
          isSetupComplete,
          isWidgetWorkbookUpToDate,
          onSave: handleSaving
        }) : navigation, /*#__PURE__*/_jsxs("section", {
          className: layoutStyles.container__main,
          children: [/*#__PURE__*/_jsx(Gallery, _objectSpread(_objectSpread({
            nameSpace: `${sectionId}-${payload.id}`,
            user: user,
            activeTemplate: payload,
            activeSectionId: sectionId,
            componentType: componentType,
            activePanel: activeGalleryPanel,
            isDataGenerating: isDataGenerating,
            templatesTag: templatesTag,
            gallerySettings: gallerySettings,
            hasDataEditorErrors: !activeSheetDataValidationResult.isComplete,
            workbookData: workbook,
            workbookErrors: workbookErrors
          }, isSmallWorkspace && !isPublic ? {
            galleryBottomMenuActions: /*#__PURE__*/_jsx(Button, {
              className: styles.submitButton,
              size: "small",
              variant: "primary",
              disabled: !hasUpdates || isLoading || !isSetupComplete,
              onClick: () => handleSaving(false),
              children: isCreating ? 'Create' : 'Save'
            })
          } : {}), {}, {
            dataEditorColumnStructure: settings.basicDataEditorColumnStructure,
            selectGalleryItem: selectGalleryItem,
            getGoogleDriveFile: getGoogleDriveFile,
            onChangeGoogleDriveFile: parseFileUpload,
            onChangeWorkbookData: handleBasicDataEditorChange,
            onSelectTemplate: template => setSelectedWidgetTemplate(template),
            onSwitchEditingMode: mode => {
              if (mode === 'advanced') {
                openAdvancedEditDataModal();
              }
            },
            onChangeTheme: (updates, sectionStyles) => {
              if (sectionStyles) {
                handleConfigChange('', sectionStyles);
              } // Make sure the theme is applied after the styles are reset


              setTimeout(() => {
                handleChange('config', updates);
              });

              if (updates.theme) {
                metrics.sendEvent('exp_theme_select', {
                  id: updates.theme
                });
              }
            },
            onSave: handleWidgetSave,
            onChange: handleChange,
            onFontsChange: handleFontsChange,
            onAiGenerate: generateData,
            onChartSelect: handleChartSelection
          })), /*#__PURE__*/_jsxs("div", {
            ref: workspaceRef,
            className: clsx(layoutStyles.container__workspace, {
              [layoutStyles['container__workspace--hasFooter']]: isSetupComplete
            }),
            children: [isSetupComplete ? /*#__PURE__*/_jsxs(Preview, _objectSpread(_objectSpread({
              className: layoutStyles.container__main__preview,
              widget: payload,
              editing: true,
              hasErrors: Boolean(workbookErrors.length),
              originFileType: (workbookFileOrigin === null || workbookFileOrigin === void 0 ? void 0 : workbookFileOrigin.originFileType) || '',
              reportingWidget: payload,
              workbookFile: workbook,
              workbookMeta: workbookMeta,
              updatedAt: updatedAt,
              initialResolutionStrategy: "cover",
              previewGroups: PREVIEW_GROUPS,
              zoomLevel: zoomLevel,
              defaultAspectRatio: CHARTS_ASPECT_RATIO[componentType],
              onPreviewResolutionChange: setLayoutResolution,
              isAccreditationHidden: true,
              isDataButtonHidden: true,
              isZoomControlHidden: true,
              isToolbarVisible: true,
              isAiExperiment: featureFlags.aiExperiment,
              onEditData: openDataEditor,
              onEditDataErrors: () => openDataEditor({
                showErrors: true
              }),
              onEditTheme: () => selectGalleryItem(GALLERY_THEME_PANEL_ID),
              onWidgetUpdate: handleChange // Hide the save button for the private chart editor
              // since it's duplicated in the primary navigation.

            }, isPublic ? {
              onSave: handleWidgetSave
            } : {}), {}, {
              children: [((activeEditingAreaMeta === null || activeEditingAreaMeta === void 0 ? void 0 : activeEditingAreaMeta.selection) && typeof (activeEditingAreaMeta === null || activeEditingAreaMeta === void 0 ? void 0 : activeEditingAreaMeta.collectionIndex) !== 'number' || activeSectionEditingArea === 'chart') && /*#__PURE__*/_jsx(PreviewTooltip, {
                actions: [{
                  title: 'Edit Data',
                  onClick: openDataEditor
                }, {
                  title: 'Select',
                  onClick: () => toggleActiveModal(modalHeaderTabs[1]),
                  hidden: isBasicEditingMode
                }]
              }), /*#__PURE__*/_jsx(ContextEditor, {
                widget: payload,
                onChange: handleConfigChange
              })]
            })) : /*#__PURE__*/_jsx(EditorGuide, {
              isWorkbookSetup: isWorkbookSetup,
              isChartSetup: isChartSetup,
              onConnectWidgetData: openDataEditor,
              onChartSelect: () => selectGalleryItem(GALLERY_CHARTS_PANEL_ID)
            }), isSetupComplete ? /*#__PURE__*/_jsxs(Form, {
              elementId: "design",
              isLoading: isLoadingWidget,
              hasUpdates: hasWidgetUpdates,
              className: clsx(layoutStyles.container__main__form, styles.form),
              children: [/*#__PURE__*/_jsx(GeneralFields, {
                widget: payload,
                sectionId: sectionId,
                sectionComponent: componentType,
                reportingWidgetId: payload.id || '',
                config: targetSection.config || {},
                title: getFormHeading(settings.heading),
                pillVariant: "compact",
                onChange: handleConfigChange,
                onChartSelect: handleChartSelection
              }), /*#__PURE__*/_jsx("div", {
                className: layoutStyles.formContent,
                children: /*#__PURE__*/_jsx(ChartFields, _objectSpread({
                  sectionComponent: componentType,
                  sectionId: sectionId,
                  widgetId: payload.id || '',
                  reportingWidgetId: payload.id || '',
                  config: targetSection.config || {},
                  pillVariant: "compact",
                  onChange: handleConfigChange
                }, !isBasicEditingMode && {
                  onEditSelections: selectionsType => toggleActiveModal(modalHeaderTabs[1], {
                    selectionsType: selectionsType ?? 'values'
                  })
                }))
              }), /*#__PURE__*/_jsx(FormFooter, {
                reviews: reviews,
                onCopyImage: handleCopyImage,
                onShare: openShareModal,
                onReviewsClick: onReviewsClick
              })]
            }) : null, isSetupComplete ? /*#__PURE__*/_jsx(CanvasControls, {
              className: layoutStyles.container__main__footer,
              zoomLevel: zoomLevel,
              adjustZoomLevel: adjustZoomLevel
            }) : null]
          })]
        }), children]
      })
    }), /*#__PURE__*/_jsx(DataModal, {
      isVisible: isEditingData,
      widget: widget,
      workbookName: WORKBOOK_NAME,
      workbookData: workbook,
      workbookMeta: workbookMeta,
      workbookUpdatedAt: workbookUpdatedAt,
      workbookSyncedAt: workbookSyncedAt,
      workbookOrigin: workbookOrigin,
      workbookSyncConfig: workbookSyncConfig,
      defaultShowErrors: defaultShowDataEditorErrors,
      defaultDataSource: defaultDataEditorSource,
      activeSelection: activeEditingAreaMeta === null || activeEditingAreaMeta === void 0 ? void 0 : activeEditingAreaMeta.selection,
      onSubmit: (wb, meta, origin) => {
        workbookLocalSave(wb, meta, origin); // Switch to advanced editing mode
        // to future updates to workbook

        setBasicEditingMode(false);
      },
      onClose: () => {
        toggleActiveModal(modalHeaderTabs[0]);
        setDefaultShowDataEditorErrors(false);
      },
      onEditSelectionsNext: openSelectionsModal
    }), /*#__PURE__*/_jsx(SelectionsModal, {
      isVisible: isEditingSelections,
      widget: payload,
      widgetId: payload.id,
      reportingWidget: widget,
      reportingWidgetId: widget.id,
      selections: activeSelections,
      activeSelectionsType: activeSelectionsType,
      labelsSelections: activeLabelsSelections,
      legendsSelections: activeLegendsSelections,
      workbookName: workbookName,
      workbookData: workbook,
      workbookMeta: workbookMeta,
      controls: selectionControls,
      isLabeled: selectionEditIsLabeled,
      strategy: selectionEditStrategy,
      headerTabs: modalHeaderTabs,
      activeHeaderTab: activeModalHeaderTab,
      onTabClick: toggleActiveModal,
      onClose: () => toggleActiveModal(modalHeaderTabs[1]),
      onSubmit: selectionsUpdate => {
        onSubmitSelection(handleChange, selectionsUpdate);
      }
    }), /*#__PURE__*/_jsx(ChartShare, {
      isVisible: isShareModalOpen,
      widget: payload,
      workbook: workbook,
      workbookMeta: workbookMeta,
      sectionId: sectionId,
      updatedAt: updatedAt,
      fileName: productName,
      layoutWidth: layoutResolution === null || layoutResolution === void 0 ? void 0 : layoutResolution.value[0],
      layoutHeight: layoutResolution === null || layoutResolution === void 0 ? void 0 : layoutResolution.value[1],
      previewCanvasRef: previewCanvasRef,
      hidePremiumOptions: !isPublic,
      onClose: () => setShareModalOpen(false),
      onImageCopy: handleCopyImageSuccess,
      onComplete: handleChartShareSuccess,
      onSignup: handleChartShareSignup
    }), /*#__PURE__*/_jsx(AuthModal, {
      isVisible: Boolean(authModalType),
      authType: authModalType,
      onChangeAuthType: changeAuthType,
      onClose: closeAuthModal,
      onAuthSuccess: handleAuthSuccess
    }), isLoading ? /*#__PURE__*/_jsx(LoadingModal, {}) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx(ConfirmationPrompt, {})]
  });
};

export default ChartEditor;

function getSectionId(payload) {
  return Object.keys(payload.sections || {})[0] || '';
}

function getFormHeading(heading) {
  // Remove 'Maker' from the heading to prevent text overflow
  return heading === null || heading === void 0 ? void 0 : heading.replace(' Maker', '');
}